import * as Sentry from '@sentry/vue';

// Runs once per page load, from bootstrap.js. No-ops with an empty DSN, which is the
// on/off switch for this - see the comment on VITE_SENTRY_DSN in .env.example. Vite bakes
// this in at build time, so flipping it requires a rebuild, not just an env edit.
if (import.meta.env.VITE_SENTRY_DSN) {
    Sentry.init({
        dsn: import.meta.env.VITE_SENTRY_DSN,
        environment: import.meta.env.MODE,
        // Talkative's chat widget ("Bobbi") throws unhandled promise rejections from inside
        // its own bundle when a request to its API does not complete - Safari reports that
        // as the generic `TypeError: Load failed`. Sentry.init() installs page-wide
        // window.onerror and window.onunhandledrejection handlers, which by design fire for
        // every script on the page, so third-party failures land here with no first-party
        // frame anywhere in the stack (BANNATYNE-SPA-V2-VUE-1K and -1Q, both iOS Safari).
        //
        // Matched on the URL of the innermost stack frame (@sentry/core's eventFilters
        // integration, _getEventFilterUrl), which for these is talkative-engage.js or
        // talkative-core.js on eu.engage.app. Our own code is unaffected: the inline loader
        // in components/live-chat-widget.blade.php runs from the page document, so its
        // frames carry the page URL, not these.
        //
        // NOT filtered on the message: "Load failed" is Safari's wording for ANY failed
        // fetch, so ignoring it by text would also hide genuine failures of our own
        // requests on iOS - the exact signal captureUnlessPageLeft() below exists to keep
        // honest. See docs/talkative-widget-error-report.md.
        denyUrls: [/eu\.engage\.app/, /talkative-cdn\.com/, /talkative-(engage|core)\.js/],
        // Opayo's sagepay.js (the card form's parent-page script, payment page only) throws
        // from inside its own window 'message' listener whenever a message arrives from any
        // origin but its own. Loqate's sensor.js (master.blade.php) posts one to `parent` on
        // every visibilitychange and pagehide - and on a top-level page `parent` is the window
        // itself - so each time a customer backgrounds or leaves the payment page, Opayo throws.
        // Harmless: the stray message is rejected as designed, and Opayo's own messages still
        // arrive from the trusted origin. BANNATYNE-SPA-V2-VUE-2: ~11,000 events in its first
        // three weeks, most of this project's volume. See docs/payment-integration.md §15.
        //
        // Matched on the exact message, which is unique to Opayo's frame messenger, NOT by
        // denyUrls on sagepay.js - that would also hide genuine failures in the card form.
        ignoreErrors: [/^Message is not from trusted origin$/],
        // Dropped because it cannot work here: @sentry/vue's init() always adds this
        // integration, and it needs an `app` at init time to attach to. This page has many
        // roots, created later and conditionally (see attachVueErrorHandler below), so
        // there is no app to hand over. Left in, it warned on every production page load
        // and then did nothing. Filtering it by name rather than switching to
        // @sentry/browser keeps this package's Vue-aware serialisation, which collapses
        // view models and VNodes in a payload instead of dumping the reactive object.
        integrations: (defaults) => defaults.filter((integration) => integration.name !== 'Vue'),
    });
}

export { Sentry };

// The page is made of several independent Vue root apps (see the various */main.js
// entries), each mounted separately. Vue swallows component errors into its own console
// logger by default and never lets them reach window.onerror, so each app needs this
// wired individually to actually reach Sentry - a single global Sentry.init() is not
// enough on its own.
export function attachVueErrorHandler(app) {
    app.config.errorHandler = (err, instance, info) => {
        Sentry.captureException(err, { extra: { info } });
        console.error(err);
    };
}

// Whether the customer has started leaving - navigated away or backgrounded the tab.
// Sticky: a request that failed while hidden may deliver its rejection after they return.
let pageLeft = false;

window.addEventListener('pagehide', () => {
    pageLeft = true;
});

document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
        pageLeft = true;
    }
});

/**
 * Report a fire-and-forget request's failure, unless the page was on its way out when it
 * failed. WebKit surfaces a request aborted by navigation as an axios `Network Error`,
 * identical at the call site to the endpoint being down - issue BANNATYNE-SPA-V2-VUE-6 was
 * 177 of those in a day, all iOS, none of them a real fault.
 *
 * @param {Error} error
 * @param {object} context - extra detail for the Sentry event, e.g. which request it was.
 */
export function captureUnlessPageLeft(error, context = {}) {
    if (pageLeft || document.visibilityState === 'hidden') {
        return;
    }

    Sentry.captureException(error, { extra: context });
}
